import { Driver, driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { hints as createHints } from 'driver.js/hints';
import 'driver.js/dist/hints.css';
import { useCallback, useEffect, useRef } from 'react';
import { t } from 'utils/getTranslations';
import { LearningHint } from './learningHints';
import { useLearningMode } from './useLearningMode';

const POPOVER_CLASS = 'learning-popover';

type Options = {
    /** False while the page is still loading, so the beacons are placed once the anchors exist. */
    ready?: boolean;
};

/**
 * Pins a pulsing beacon next to each hint's anchor while learning mode is on, and offers the same
 * hints as a step-by-step tour. Hints whose anchor is not in the DOM are skipped silently, so a
 * page can declare hints for elements that only exist in some states.
 */
export function useLearningHints(learningHints: LearningHint[], { ready = true }: Options = {}) {
    const { isEnabled: isLearningMode } = useLearningMode();
    const isActive = isLearningMode && ready;
    const tourRef = useRef<Driver | null>(null);

    useEffect(() => {
        if (!isActive) return;
        const pageHints = createHints({
            hints: learningHints.map((hint) => ({
                element: hint.element,
                beacon: { side: hint.side ?? 'top', align: hint.align ?? 'end' },
                popover: {
                    title: hint.title,
                    description: hint.description,
                    side: hint.side,
                    align: hint.align,
                },
            })),
            buttonText: t('learning-mode.got-it'),
            popoverClass: POPOVER_CLASS,
        });
        pageHints.show();
        return () => {
            pageHints.hide();
            tourRef.current?.destroy();
            tourRef.current = null;
        };
    }, [isActive, learningHints]);

    const startTour = useCallback(() => {
        if (!isActive) return;
        tourRef.current?.destroy();
        const tour = driver({
            steps: learningHints.map((hint) => ({
                element: hint.element,
                popover: {
                    title: hint.title,
                    description: hint.description,
                    side: hint.side,
                    align: hint.align,
                },
            })),
            skipMissingElement: true,
            showProgress: true,
            popoverClass: POPOVER_CLASS,
            nextBtnText: t('learning-mode.next'),
            prevBtnText: t('learning-mode.previous'),
            doneBtnText: t('learning-mode.done'),
        });
        tourRef.current = tour;
        tour.drive();
    }, [isActive, learningHints]);

    return { isActive, startTour };
}
